import type { AppVueRouter } from '@modules/core/services/router/types';
import type { TranslationKeysMapper } from '@modules/i18n/types';
import type { AppMenu } from '@modules/sidebar/utils';
import { AppModule } from '@modules/core/plugins/AppModule';
import { useI18n } from '@modules/i18n/composables';
import {
  ADMIN_PARTNERS_MENU_ITEM,
  AFFILIATE_TRACKING_LINKS_MENU_ITEM,
  TRANSLATION_GROUP_SIDEBAR,
} from '@modules/sidebar/constants';
import {
  ADMIN_WEBHOOKS_HISTORY_LIST_ROUTE_NAME,
  ADMIN_WEBHOOKS_HISTORY_ROUTE_NAME,
  AFFILIATE_WEBHOOKS_HISTORY_LIST_ROUTE_NAME,
  AFFILIATE_WEBHOOKS_HISTORY_ROUTE_NAME,
  TRANSLATION_GROUP_ADMIN_WEBHOOKS_HISTORY,
  TRANSLATION_GROUP_AFFILIATES_WEBHOOKS_HISTORY,
  WEBHOOKS_HISTORY_PERMISSIONS,
} from '@modules/webhooks-history/constants';
import {
  WebhooksHistoryDetailsModule,
} from '@modules/webhooks-history/submodules/history-details/WebhooksHistoryDetailsModule';
import { getTranslationAdminKeys, getTranslationAffiliatesKeys } from '@modules/webhooks-history/translations';

export class WebhooksHistoryModule extends AppModule {
  protected submodules = [
    new WebhooksHistoryDetailsModule(),
  ];

  addRoutes(router: AppVueRouter): void {
    router.addAdminRoute({
      name: ADMIN_WEBHOOKS_HISTORY_ROUTE_NAME,
      path: 'webhooks-history',
      component: () => import('@modules/core/components/SimpleChildRoutePage/SimpleChildRoutePage.vue'),
      keepAliveInclude: 'WebhooksHistoryListPage',
      permission: WEBHOOKS_HISTORY_PERMISSIONS.SHOW,
    });

    router.addChildRoute(ADMIN_WEBHOOKS_HISTORY_ROUTE_NAME, {
      name: ADMIN_WEBHOOKS_HISTORY_LIST_ROUTE_NAME,
      path: '',
      component: () => import('@modules/webhooks-history/pages/WebhooksHistoryListPage/WebhooksHistoryListPage.vue'),
    });

    router.addAffiliateRoute({
      name: AFFILIATE_WEBHOOKS_HISTORY_ROUTE_NAME,
      path: 'webhooks-history',
      component: () => import('@modules/core/components/SimpleChildRoutePage/SimpleChildRoutePage.vue'),
      keepAliveInclude: 'WebhooksHistoryListPage',
      permission: WEBHOOKS_HISTORY_PERMISSIONS.SHOW,
    });

    router.addChildRoute(AFFILIATE_WEBHOOKS_HISTORY_ROUTE_NAME, {
      name: AFFILIATE_WEBHOOKS_HISTORY_LIST_ROUTE_NAME,
      path: '',
      component: () => import('@modules/webhooks-history/pages/WebhooksHistoryListPage/WebhooksHistoryListPage.vue'),
    });
  }

  addMenu(menu: AppMenu): void {
    const { getTPath } = useI18n();

    menu.addChildAdminMenuItem(ADMIN_PARTNERS_MENU_ITEM, {
      caption: getTPath(TRANSLATION_GROUP_SIDEBAR, 'PARTNERS_ADMIN_WEBHOOKS_HISTORY'),
      to: {
        name: ADMIN_WEBHOOKS_HISTORY_LIST_ROUTE_NAME,
      },
      id: ADMIN_WEBHOOKS_HISTORY_LIST_ROUTE_NAME,
      permission: WEBHOOKS_HISTORY_PERMISSIONS.SHOW,
      order: 50,
    });

    menu.addChildAffiliateMenuItem(AFFILIATE_TRACKING_LINKS_MENU_ITEM, {
      caption: getTPath(TRANSLATION_GROUP_SIDEBAR, 'TRACKING_LINKS_WEBHOOKS_HISTORY'),
      to: {
        name: AFFILIATE_WEBHOOKS_HISTORY_LIST_ROUTE_NAME,
      },
      id: AFFILIATE_WEBHOOKS_HISTORY_LIST_ROUTE_NAME,
      permission: WEBHOOKS_HISTORY_PERMISSIONS.SHOW,
      order: 40,
    });
  }

  addTranslationKeys(mapper: TranslationKeysMapper): void {
    mapper.addTranslationsMap(TRANSLATION_GROUP_ADMIN_WEBHOOKS_HISTORY, getTranslationAdminKeys);
    mapper.addTranslationsMap(TRANSLATION_GROUP_AFFILIATES_WEBHOOKS_HISTORY, getTranslationAffiliatesKeys);
  }
}
